import {
    useIsMutating,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { integrationCoaMappingContract } from "@common/contracts/integration-coa-mapping.contract";
import { IntegrationCoaMapping } from "@common/schemas/domains/accounting/integrationCoaMapping.schema";
import { ConnectionProvider } from "@common/types/integrationConnection";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { didRequestReachServer } from "./apiError";

const integrationCoaMappingClient = getBackendAPIClient(
    integrationCoaMappingContract,
);

interface IntegrationCoaMappingsScope {
    entityId: number | undefined;
    provider: ConnectionProvider | undefined;
}

export const integrationCoaMappingQueryKeys = {
    all: ["integration-coa-mappings"] as const,
    byEntityAndProvider: ({
        entityId,
        provider,
    }: IntegrationCoaMappingsScope) =>
        [...integrationCoaMappingQueryKeys.all, entityId, provider] as const,
    update: ({ entityId, provider }: IntegrationCoaMappingsScope) =>
        [
            ...integrationCoaMappingQueryKeys.all,
            "update",
            entityId,
            provider,
        ] as const,
};

export function useIntegrationCoaMappings({
    entityId,
    provider,
}: IntegrationCoaMappingsScope) {
    return useQuery({
        queryKey: integrationCoaMappingQueryKeys.byEntityAndProvider({
            entityId,
            provider,
        }),
        queryFn: async (): Promise<IntegrationCoaMapping[]> => {
            if (entityId === undefined || provider === undefined) {
                return [];
            }
            const response = await integrationCoaMappingClient.getMappings({
                params: { entityId, provider },
            });
            return response.body.mappings;
        },
        enabled: entityId !== undefined && provider !== undefined,
    });
}

interface UpdateIntegrationCoaMappingVariables {
    mappingId: string;
    accountId: string | null;
}

export function useUpdateIntegrationCoaMapping({
    entityId,
    provider,
}: IntegrationCoaMappingsScope) {
    const queryClient = useQueryClient();
    const { error: toastError } = useToaster();
    const queryKey = integrationCoaMappingQueryKeys.byEntityAndProvider({
        entityId,
        provider,
    });

    return useMutation({
        mutationKey: integrationCoaMappingQueryKeys.update({
            entityId,
            provider,
        }),
        mutationFn: async ({
            mappingId,
            accountId,
        }: UpdateIntegrationCoaMappingVariables) => {
            if (entityId === undefined) {
                throw new Error("Entity ID is required");
            }
            const response = await integrationCoaMappingClient.updateMapping({
                params: { entityId, mappingId },
                body: { accountId },
            });
            return response.body;
        },
        onSuccess: (updatedMapping) => {
            queryClient.setQueryData<IntegrationCoaMapping[]>(
                queryKey,
                (mappings) =>
                    mappings?.map((mapping) =>
                        mapping.id === updatedMapping.id
                            ? updatedMapping
                            : mapping,
                    ),
            );
        },
        onError: (error) => {
            if (!didRequestReachServer(error)) {
                toastError("Couldn't update the account mapping. Try again.");
            }
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey }),
    });
}

export function useIsUpdatingIntegrationCoaMapping(
    scope: IntegrationCoaMappingsScope,
): boolean {
    return (
        useIsMutating({
            mutationKey: integrationCoaMappingQueryKeys.update(scope),
        }) > 0
    );
}
